import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { FontFamily, Tool } from '@/types'

export type Theme = 'system' | 'light' | 'dark'
export type SidebarTab = 'pages' | 'outline' | 'comments'
export type FitMode = 'width' | 'page' | null

export interface ToolStyle {
  color: string
  width: number
  opacity: number
  fill: string | null
  fontSize: number
  fontFamily: FontFamily
  bold: boolean
  italic: boolean
}

const style = (p: Partial<ToolStyle>): ToolStyle => ({
  color: '#111827',
  width: 2,
  opacity: 1,
  fill: null,
  fontSize: 14,
  fontFamily: 'sans',
  bold: false,
  italic: false,
  ...p,
})

export const DEFAULT_STYLES: Partial<Record<Tool, ToolStyle>> = {
  pen: style({ color: '#1d4ed8', width: 2.5 }),
  marker: style({ color: '#facc15', width: 14, opacity: 0.5 }),
  highlight: style({ color: '#fde047', opacity: 1 }),
  underline: style({ color: '#16a34a' }),
  strikeout: style({ color: '#dc2626' }),
  rect: style({ color: '#dc2626', width: 2 }),
  ellipse: style({ color: '#dc2626', width: 2 }),
  line: style({ color: '#dc2626', width: 2 }),
  arrow: style({ color: '#dc2626', width: 2.5 }),
  text: style({ color: '#111827', fontSize: 14 }),
  note: style({ color: '#facc15' }),
}

export const SWATCHES = [
  '#111827',
  '#ffffff',
  '#dc2626',
  '#f97316',
  '#facc15',
  '#16a34a',
  '#0891b2',
  '#2563eb',
  '#7c3aed',
  '#db2777',
]
export const HIGHLIGHT_SWATCHES = ['#fde047', '#86efac', '#7dd3fc', '#f9a8d4', '#fdba74', '#c4b5fd']

interface UiState {
  theme: Theme
  tool: Tool
  styles: Partial<Record<Tool, ToolStyle>>
  zoom: number
  fit: FitMode
  sidebarOpen: boolean
  sidebarTab: SidebarTab
  selection: string[]
  /** page index (0-based) most visible in the viewport */
  currentPage: number
  /** set to request the viewer scroll to a page */
  scrollRequest: { index: number; top?: number; nonce: number } | null
  searchOpen: boolean
  nightMode: boolean
  organizeMode: boolean
  author: string
  /** when a stylus is detected, finger touches scroll instead of drawing */
  stylusDetected: boolean
  /** remember the selected stamp/signature for click-to-place */
  pendingImage: {
    src: string
    width: number
    height: number
    kind: 'signature' | 'initials' | 'image' | 'stamp'
  } | null
  editingId: string | null
  dialog: null | 'signature' | 'shortcuts' | 'password' | 'about' | 'settings'
}

interface UiActions {
  setTheme(t: Theme): void
  setTool(t: Tool): void
  setStyle(tool: Tool, patch: Partial<ToolStyle>): void
  styleFor(tool: Tool): ToolStyle
  setZoom(z: number, fit?: FitMode): void
  setFit(f: FitMode): void
  toggleSidebar(open?: boolean): void
  setSidebarTab(t: SidebarTab): void
  select(ids: string[]): void
  setCurrentPage(i: number): void
  goToPage(i: number, top?: number): void
  setSearchOpen(v: boolean): void
  toggleNight(): void
  setOrganize(v: boolean): void
  setAuthor(a: string): void
  setStylusDetected(v: boolean): void
  setPendingImage(p: UiState['pendingImage']): void
  setEditing(id: string | null): void
  openDialog(d: UiState['dialog']): void
}

export const MIN_ZOOM = 0.1
export const MAX_ZOOM = 8

export const useUi = create<UiState & UiActions>()(
  persist(
    (set, get) => ({
      theme: 'system',
      tool: 'select',
      styles: DEFAULT_STYLES,
      zoom: 1,
      fit: 'width',
      sidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
      sidebarTab: 'pages',
      selection: [],
      currentPage: 0,
      scrollRequest: null,
      searchOpen: false,
      nightMode: false,
      organizeMode: false,
      author: '',
      stylusDetected: false,
      pendingImage: null,
      editingId: null,
      dialog: null,

      setTheme: (theme) => {
        document.documentElement.dataset.theme = theme
        set({ theme })
      },
      setTool: (tool) => set({ tool, selection: tool === 'select' ? get().selection : [], editingId: null }),
      setStyle: (tool, patch) =>
        set((s) => ({ styles: { ...s.styles, [tool]: { ...get().styleFor(tool), ...patch } } })),
      styleFor: (tool) => get().styles[tool] ?? DEFAULT_STYLES[tool] ?? style({}),
      setZoom: (z, fit = null) => set({ zoom: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z)), fit }),
      setFit: (fit) => set({ fit }),
      toggleSidebar: (open) => set((s) => ({ sidebarOpen: open ?? !s.sidebarOpen })),
      setSidebarTab: (sidebarTab) => set({ sidebarTab, sidebarOpen: true }),
      select: (selection) => set({ selection }),
      setCurrentPage: (currentPage) => set({ currentPage }),
      goToPage: (index, top) => set({ scrollRequest: { index, top, nonce: Math.random() } }),
      setSearchOpen: (searchOpen) => set({ searchOpen }),
      toggleNight: () => set((s) => ({ nightMode: !s.nightMode })),
      setOrganize: (organizeMode) => set({ organizeMode, selection: [] }),
      setAuthor: (author) => set({ author }),
      setStylusDetected: (stylusDetected) => set({ stylusDetected }),
      setPendingImage: (pendingImage) => set({ pendingImage }),
      setEditing: (editingId) => set({ editingId }),
      openDialog: (dialog) => set({ dialog }),
    }),
    {
      name: 'folio-ui',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        theme: s.theme,
        styles: s.styles,
        sidebarOpen: s.sidebarOpen,
        sidebarTab: s.sidebarTab,
        nightMode: s.nightMode,
        author: s.author,
        stylusDetected: s.stylusDetected,
      }),
    },
  ),
)
